import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Info, Loader2, RefreshCw } from "lucide-react";
import toast from "react-hot-toast";
import { DsIcon } from "../../../design-system";
import PayrollStepActions from "../payroll/PayrollStepActions";
import PayrollActionChooserModal from "../payroll/PayrollActionChooserModal";
import { chunkIds, formatPayMonthLabel } from "../payroll/runPayrollUtils";
import ProcessAttendanceStepBody, {
  ProcessAttendanceActions,
  FinaliseAttendanceActions,
} from "./ProcessAttendanceStepViews";
import ProcessWorkflowShell from "../shared/ProcessWorkflowShell";
import ProcessWizardStrip from "../shared/ProcessWizardStrip";
import {
  persistProcessWizardLanding,
  resolveProcessWizardLanding,
} from "../shared/processWizardLanding";
import PayMonthNav from "../shared/PayMonthNav";
import {
  createInitialAttendanceProcessState,
  getAttendanceProcessSteps,
} from "../../../constants/attendanceProcessCatalog";
import {
  loadAttendancePolicies,
  POLICIES_STORAGE_KEY,
  resolvePolicyForEmployee,
  isEmployeeManualAttendanceProcess,
} from "../../../lib/attendancePolicyEngine";
import { resolveActivityWorkflow } from "../../../lib/attendanceActivityWorkflows";
import { isPolicyOverrideAttendance } from "../../../lib/attendancePolicyConfig";
import { attendanceService, attendanceStructureService } from "../../../services/api";
import { resolveCompanyMinMonthValue, clampYm } from "../../../utils/selectableDateBounds";

/**
 * Same engine. Fewer /run calls as the selection grows; cap people per call at 32
 * so the ring still moves and a failed batch is not huge. Limit parallel HTTP so
 * the DB pool is not flooded (backend already runs up to 8 employees inside one /run).
 */
const ATTENDANCE_PROCESS_MAX_PER_CALL = 32;

function processChunkSize(n) {
  const count = Math.max(0, Number(n) || 0);
  if (count <= 1) return 1;
  if (count <= 32) return Math.min(3, count);
  if (count <= 100) {
    const t = (count - 33) / (100 - 33);
    return Math.round(8 + t * (15 - 8));
  }
  if (count <= 500) {
    const t = (count - 100) / (500 - 100);
    return Math.round(20 + t * (ATTENDANCE_PROCESS_MAX_PER_CALL - 20));
  }
  return ATTENDANCE_PROCESS_MAX_PER_CALL;
}

function processPoolConcurrency(n, chunkSize) {
  const count = Math.max(0, Number(n) || 0);
  const size = Math.max(1, Number(chunkSize) || 1);
  const waves = Math.max(1, Math.ceil(count / size) || 1);
  // Small 2–3 person calls: up to 3 HTTP. Larger calls: 2 HTTP only.
  const maxHttp = size >= 8 ? 2 : 3;
  return Math.min(maxHttp, waves);
}

async function runChunksInPool(chunks, concurrency, worker) {
  const list = chunks || [];
  if (!list.length) return;
  const conc = Math.max(1, Math.min(concurrency || 1, list.length));
  let i = 0;
  await Promise.all(
    Array.from({ length: conc }, async () => {
      while (i < list.length) {
        const idx = i;
        i += 1;
        await worker(list[idx], idx);
      }
    })
  );
}

/**
 * After all in-scope employees are processed, pre-process steps lock (payroll pattern).
 * `process` stays editable so Reprocess / Unprocess remain available until month finalise.
 */
const ATTENDANCE_POST_PROCESS_KEYS = new Set([
  "process",
  "checklist",
  "preview_validate",
  "finalise",
]);
const ATTENDANCE_REVIEW_WHEN_LOCKED_KEYS = new Set(["preview_validate"]);
const ATTENDANCE_REQUIRES_FULL_PROCESS_KEYS = new Set([
  "checklist",
  "preview_validate",
  "finalise",
]);

function isAttendancePostProcessStep(key) {
  return ATTENDANCE_POST_PROCESS_KEYS.has(key);
}

function attendanceProcessEmployeeId(employee) {
  const n = Number(employee?.id ?? employee?.employeeId);
  return Number.isFinite(n) && n > 0 ? String(n) : "";
}

const PROCESS_FLOW_HINT =
  "Change Status → Leave → LOP → Reconciliation → Process → Checklist → Preview → Finalise";

/** Extra how-to copy that currently sits as body paragraphs (kept in the step info popover). */
const STEP_BODY_HINTS = {
  change_status:
    "Select one or more employees, then open the calendar. Use Previous / Next on the calendar to move through only this selection and change day statuses.",
  apply_leave:
    "Punch / automated-policy employees only. Auto-present (Manual Override) are excluded.",
  finalise:
    "Finalise locks the full company attendance month for payroll. All in-scope employees must be processed first.",
};

function stepHintText(step) {
  const parts = [step?.description, step?.bodyHint, STEP_BODY_HINTS[step?.key]].filter(Boolean);
  return parts.join("\n\n");
}

function ProcessHintInfo({ text, darkMode = false, ariaLabel = "Info" }) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 288, maxHeight: 220 });

  useLayoutEffect(() => {
    if (!open) return;
    const btn = btnRef.current;
    if (!btn) return;
    const place = () => {
      const rect = btn.getBoundingClientRect();
      const menuWidth = 288;
      const margin = 8;
      const maxH = Math.min(Math.round(window.innerHeight * 0.4), 220);
      let left = rect.left;
      left = Math.max(margin, Math.min(left, window.innerWidth - menuWidth - margin));
      let top = rect.bottom + 4;
      if (top + 80 > window.innerHeight - margin) {
        top = Math.max(margin, rect.top - maxH - 4);
      }
      setCoords({ top, left, maxHeight: maxH, width: menuWidth });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open]);

  if (!text) return null;

  return (
    <div className="relative shrink-0" ref={btnRef}>
      <button
        type="button"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        className={`rounded p-1 text-[hsl(var(--ds-muted-fg))] ${
          darkMode
            ? "hover:bg-[hsl(var(--ds-muted))]"
            : "hover:bg-[hsl(var(--ds-muted))]"
        }`}
        aria-label={ariaLabel}
        
      >
        <DsIcon icon={Info} size="sm" />
      </button>
      {open &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            data-process-hint-info
            role="tooltip"
            className={`fixed z-[10040] overflow-y-auto whitespace-pre-line rounded-md border p-3 text-left text-xs leading-snug shadow-lg ${
              darkMode
                ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-muted-fg))]"
                : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-muted-fg))]"
            }`}
            style={{
              top: coords.top,
              left: coords.left,
              width: coords.width || 288,
              maxHeight: coords.maxHeight || 220,
            }}
          >
            {text}
          </div>,
          document.body
        )}
    </div>
  );
}

/**
 * Process & Finalise Attendance — left STEPS · right step panel · PayrollStepActions.
 * Engine sync runs via Process / Reprocess / Unprocess (no standalone Run Attendance / runBusy).
 */
export default function ProcessAttendancePanel({
  darkMode = false,
  payMonth,
  isLocked = false,
  onLockMonth = null,
  onRequestUnlock = null,
  employees = [],
  statusColumns = [],
  leaveCodes = [],
  getStatusCounts = null,
  getPayDays = null,
  processCountsRevision = 0,
  onEnsureAttendanceCounts = null,
  onOpenCalendar = null,
  onOpenCalendarQueue = null,
  onApplyLeave = null,
  onLopReversal = null,
  onExportExcel = null,
  onPayMonthChange = null,
  masterLists = {},
  legacyRegister = null,
  /** "process" = finalise workflow; "actions" = action cards hub */
  defaultView = "actions",
  actionsHub = null,
  /** When false, hide the Process workflow pill (Actions-only roles). */
  canProcessWorkflow = true,
}) {
  const policyAsOfDate = useMemo(() => {
    const m = String(payMonth || "").match(/^(\d{4})-(\d{2})$/);
    if (!m) return new Date();
    // Mid-month anchor to evaluate effective-date based policy assignments month-wise.
    return new Date(Number(m[1]), Number(m[2]) - 1, 15);
  }, [payMonth]);

  const companyMinMonth = useMemo(() => resolveCompanyMinMonthValue(), []);
  /** Temporary cap: current calendar month only. Payroll open-month max comes later. */
  const companyMaxMonth = useMemo(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  }, []);

  const handlePayMonthChange = (next) => {
    onPayMonthChange?.(clampYm(next, companyMinMonth, companyMaxMonth));
  };

  useEffect(() => {
    if (!payMonth) return;
    const current = String(payMonth).slice(0, 7);
    const clamped = clampYm(current, companyMinMonth, companyMaxMonth);
    if (clamped && clamped !== current) {
      onPayMonthChange?.(clamped);
    }
  }, [payMonth, companyMinMonth, companyMaxMonth, onPayMonthChange]);

  const [panelView, setPanelView] = useState(() => {
    if (!canProcessWorkflow) return "actions";
    return defaultView === "process" ? "process" : "actions";
  });

  useEffect(() => {
    if (!canProcessWorkflow && panelView === "process") {
      setPanelView("actions");
    }
  }, [canProcessWorkflow, panelView]);

  useEffect(() => {
    if (!canProcessWorkflow) {
      setPanelView("actions");
      return;
    }
    if (defaultView === "actions" || defaultView === "register" || defaultView === "process") {
      setPanelView(defaultView === "register" ? "actions" : defaultView);
    }
  }, [defaultView, canProcessWorkflow]);
  const [policies, setPolicies] = useState(() =>
    loadAttendancePolicies().filter((p) => p?.status !== "inactive")
  );

  useEffect(() => {
    const syncPolicies = () => {
      setPolicies(loadAttendancePolicies().filter((p) => p?.status !== "inactive"));
    };
    syncPolicies();
    window.addEventListener("storage", syncPolicies);
    window.addEventListener("weeklyOffPolicyUpdated", syncPolicies);
    window.addEventListener("attendanceMappingsUpdated", syncPolicies);
    window.addEventListener("hrms-attendance-policy-updated", syncPolicies);
    return () => {
      window.removeEventListener("storage", syncPolicies);
      window.removeEventListener("weeklyOffPolicyUpdated", syncPolicies);
      window.removeEventListener("attendanceMappingsUpdated", syncPolicies);
      window.removeEventListener("hrms-attendance-policy-updated", syncPolicies);
    };
  }, []);

  // Pull latest policy master from DB so attendance processing stays policy-driven month-wise.
  useEffect(() => {
    let cancelled = false;
    const pullPoliciesFromServer = async () => {
      try {
        const res = await attendanceStructureService.getAttendancePolicies();
        const apiList = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
        if (apiList.length && typeof localStorage !== "undefined") {
          localStorage.setItem(POLICIES_STORAGE_KEY, JSON.stringify(apiList));
        }
        if (!cancelled) {
          setPolicies(loadAttendancePolicies().filter((p) => p?.status !== "inactive"));
        }
      } catch {
        // Fall back to latest local cache if API is unavailable.
      }
    };
    pullPoliciesFromServer();
    return () => {
      cancelled = true;
    };
  }, [payMonth]);

  const [scopeMode, setScopeMode] = useState("all");
  const [selectedPolicyIds, setSelectedPolicyIds] = useState([]);
  const [processStatus, setProcessStatus] = useState(null);
  const [processStatusLoading, setProcessStatusLoading] = useState(false);
  const [lastRunSummary, setLastRunSummary] = useState(null);
  const [actionChooser, setActionChooser] = useState({ open: false, mode: null });
  const [batchProgress, setBatchProgress] = useState(null);
  const autoLandingMonthRef = useRef(null);

  const payMonthParts = useMemo(() => {
    const m = String(payMonth || "").match(/^(\d{4})-(\d{2})$/);
    if (!m) return null;
    return { year: Number(m[1]), month: Number(m[2]) };
  }, [payMonth]);

  const refreshProcessStatus = async () => {
    if (!payMonthParts) return null;
    setProcessStatusLoading(true);
    try {
      const res = await attendanceService.getAttendanceProcessStatus(
        payMonthParts.month,
        payMonthParts.year
      );
      const data = res?.data || res;
      setProcessStatus(data || null);
      return data || null;
    } catch (err) {
      console.warn("Failed to load attendance process status:", err);
      setProcessStatus(null);
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Could not load attendance process status";
      toast.error(msg);
      return null;
    } finally {
      setProcessStatusLoading(false);
    }
  };

  const finalizationWorkflow = useMemo(() => {
    const list = Array.isArray(policies) ? policies : [];
    const active = list.filter((p) => p && p.status !== "inactive");
    const p = active.find((x) => x.isDefault) || active[0] || null;
    return resolveActivityWorkflow(p, "attendanceFinalization");
  }, [policies]);
  const workflowMethod = finalizationWorkflow?.approvalMethod || "passkey";

  const [pendingFinalizationRequests, setPendingFinalizationRequests] = useState({
    lock: null,
    unlock: null,
  });

  const refreshPendingFinalizationRequests = useCallback(async () => {
    if (!payMonthParts) return;
    try {
      const res = await attendanceService.listDeskQuickRequests({ status: "Pending" });
      const rows = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
      let lockReq = null;
      let unlockReq = null;
      for (const r of rows) {
        const al = String(r.actionLabel || "").trim();
        const rMonth = Number(r.formData?.month || r.formData?.data?.month);
        const rYear = Number(r.formData?.year || r.formData?.data?.year);
        const rPayMonth = String(r.formData?.payMonth || r.formData?.data?.payMonth || "");
        const matchesMonth =
          (rMonth === payMonthParts.month && rYear === payMonthParts.year) ||
          rPayMonth === payMonth;
        if (!matchesMonth) continue;
        if (al === "Attendance Lock" && !lockReq) lockReq = r;
        if (al === "Attendance Unlock" && !unlockReq) unlockReq = r;
      }
      setPendingFinalizationRequests({ lock: lockReq, unlock: unlockReq });
    } catch {
      // ignore
    }
  }, [payMonthParts, payMonth]);

  useEffect(() => {
    if (!payMonthParts) return;
    refreshProcessStatus();
    refreshPendingFinalizationRequests();
  }, [payMonthParts?.month, payMonthParts?.year, isLocked, refreshPendingFinalizationRequests]);

  useEffect(() => {
    const handleQuickReqUpdate = () => {
      refreshPendingFinalizationRequests();
    };
    window.addEventListener("hrms-attendance-quick-request-updated", handleQuickReqUpdate);
    return () => {
      window.removeEventListener("hrms-attendance-quick-request-updated", handleQuickReqUpdate);
    };
  }, [refreshPendingFinalizationRequests]);

  /**
   * Process universe = Attendance FE list (already excludes External) ∩ server scope IDs.
   * Do not merge processStatus.employees — that inflated counts beyond the register.
   */
  const companyScopeEmployees = useMemo(() => {
    const list = Array.isArray(employees) ? employees : [];
    const scopeIds = new Set(
      (processStatus?.scopeEmployeeIds || []).map((id) => String(id))
    );
    if (!scopeIds.size) return list;
    return list.filter((e) => scopeIds.has(String(e.id)));
  }, [employees, processStatus?.scopeEmployeeIds]);

  const scopedEmployees = useMemo(() => {
    const list = companyScopeEmployees;
    if (scopeMode !== "selected" || !selectedPolicyIds?.length) return list;
    const idSet = new Set(selectedPolicyIds.map(String));
    return list.filter((e) => {
      const p = resolvePolicyForEmployee(e, policies, policyAsOfDate);
      const pid = String(p?.id ?? p?.policyId ?? p?.name ?? "");
      return idSet.has(pid);
    });
  }, [companyScopeEmployees, scopeMode, selectedPolicyIds, policies, policyAsOfDate]);

  const includeApplyLeave = useMemo(() => {
    if (!scopedEmployees.length) {
      const active = policies;
      if (!active.length) return true;
      return active.some((p) => !isPolicyOverrideAttendance(p));
    }
    return scopedEmployees.some(
      (e) => !isEmployeeManualAttendanceProcess(e, policyAsOfDate, policies)
    );
  }, [scopedEmployees, policies, policyAsOfDate]);

  const [processState, setProcessState] = useState(() =>
    createInitialAttendanceProcessState(payMonth, { includeApplyLeave })
  );
  const [visitedKeys, setVisitedKeys] = useState(() => new Set(["change_status"]));
  const [stepBusy, setStepBusy] = useState(false);

  useEffect(() => {
    if (!payMonth) return;
    setProcessState((prev) => {
      if (prev.payMonth === payMonth) {
        const catalog = getAttendanceProcessSteps(includeApplyLeave);
        const keys = catalog.map((s) => s.key).join(",");
        const prevKeys = (prev.steps || []).map((s) => s.key).join(",");
        if (keys === prevKeys) return prev;
        const statusByKey = Object.fromEntries(
          (prev.steps || []).map((s) => [s.key, s.status])
        );
        return {
          ...prev,
          steps: catalog.map((s) => ({
            ...s,
            status: statusByKey[s.key] || "pending",
            resultJson: null,
          })),
          activeStepKey: catalog.some((s) => s.key === prev.activeStepKey)
            ? prev.activeStepKey
            : catalog[0]?.key,
        };
      }
      return createInitialAttendanceProcessState(payMonth, { includeApplyLeave });
    });
  }, [payMonth, includeApplyLeave]);

  /** Month lock (API) or finalise ⇒ all steps show as passed / green (not draft). */
  useEffect(() => {
    if (!isLocked) return;
    setProcessState((prev) => {
      const stepsList = prev.steps || [];
      const allPassed =
        stepsList.length > 0 &&
        stepsList.every((s) => s.status === "passed" || s.status === "acknowledged");
      if (allPassed && prev.status === "finalised" && prev.finalisedAt) return prev;
      return {
        ...prev,
        status: "finalised",
        finalisedAt: prev.finalisedAt || new Date().toISOString(),
        steps: stepsList.map((s) => ({
          ...s,
          status:
            s.status === "passed" || s.status === "acknowledged" ? s.status : "passed",
        })),
      };
    });
  }, [isLocked, payMonth]);

  // Prevent "finalised" badge leakage when switching months or after unlock.
  useEffect(() => {
    if (isLocked) return;
    setProcessState((prev) => {
      if (!prev.finalisedAt && prev.status !== "finalised") return prev;
      return {
        ...prev,
        status: "draft",
        finalisedAt: null,
        steps: (prev.steps || []).map((s) =>
          s.key === "finalise" && (s.status === "passed" || s.status === "acknowledged")
            ? { ...s, status: "pending" }
            : s
        ),
      };
    });
  }, [isLocked, payMonth]);

  const steps = processState.steps || [];
  const activeStepKey = processState.activeStepKey;
  const activeStep = useMemo(
    () => steps.find((s) => s.key === activeStepKey) || steps[0],
    [steps, activeStepKey]
  );
  const activeIndex = Math.max(0, steps.findIndex((s) => s.key === activeStep?.key));

  const setActiveStepKey = (key) => {
    if (!key) return;
    setVisitedKeys((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
    setProcessState((prev) => ({ ...prev, activeStepKey: key }));
    const month = String(payMonth || "").slice(0, 7);
    if (month) persistProcessWizardLanding(`attendance-wizard-step:${month}`, key);
  };

  useEffect(() => {
    setVisitedKeys(new Set(["change_status"]));
  }, [payMonth]);

  const markStep = (key, status) => {
    setProcessState((prev) => {
      const finalising = key === "finalise" && status === "passed";
      return {
        ...prev,
        status: finalising ? "finalised" : prev.status,
        steps: (prev.steps || []).map((s) => {
          if (finalising) return { ...s, status: "passed" };
          if (s.key === key) return { ...s, status };
          return s;
        }),
        finalisedAt: finalising ? new Date().toISOString() : prev.finalisedAt,
      };
    });
  };

  /** Clear green/check for one step (re-Validate required). Never demote a finalised month. */
  const clearStepValidation = (key) => {
    if (!key || key === "finalise") return;
    setProcessState((prev) => {
      if (prev.status === "finalised" || prev.finalisedAt) return prev;
      return {
        ...prev,
        steps: (prev.steps || []).map((s) =>
          s.key === key ? { ...s, status: "pending", resultJson: null } : s
        ),
      };
    });
  };

  /**
   * Unprocess consent 3D: clear greens for all steps up to and including Process.
   * Checklist / Preview / Finalise follow stepsFullyLocked sync separately.
   */
  const resetStepsThroughProcess = () => {
    setProcessState((prev) => {
      const list = prev.steps || [];
      const processIdx = list.findIndex((s) => s.key === "process");
      return {
        ...prev,
        status: "draft",
        finalisedAt: null,
        steps: list.map((s, idx) => {
          if (processIdx < 0) {
            if (s.key === "process") {
              return { ...s, status: "pending", resultJson: null };
            }
            return s;
          }
          if (idx <= processIdx) {
            return { ...s, status: "pending", resultJson: null };
          }
          return s;
        }),
      };
    });
  };

  const processedIds = useMemo(
    () => (processStatus?.processedEmployeeIds || []).map(String),
    [processStatus?.processedEmployeeIds]
  );
  const processedSet = useMemo(() => new Set(processedIds), [processedIds]);
  const processedEmployees = useMemo(
    () =>
      companyScopeEmployees.filter((e) => {
        const id = attendanceProcessEmployeeId(e);
        return id && processedSet.has(id);
      }),
    [companyScopeEmployees, processedSet]
  );
  const unprocessedEmployees = useMemo(
    () =>
      companyScopeEmployees.filter((e) => {
        const id = attendanceProcessEmployeeId(e);
        return !id || !processedSet.has(id);
      }),
    [companyScopeEmployees, processedSet]
  );

  // Reload full-month attendance (with punches) so Process / Reconciliation match calendar.
  useEffect(() => {
    if (stepBusy) return;
    if (typeof onEnsureAttendanceCounts !== "function") return;
    if (
      activeStepKey !== "process" &&
      activeStepKey !== "preview_validate" &&
      activeStepKey !== "reconciliation"
    ) {
      return;
    }
    const ids =
      processedEmployees.length > 0
        ? processedEmployees.map((e) => e.id)
        : companyScopeEmployees.map((e) => e.id);
    if (!ids.length) return;
    let cancelled = false;
    (async () => {
      await onEnsureAttendanceCounts(ids);
      if (cancelled) return;
    })();
    return () => {
      cancelled = true;
    };
  }, [
    activeStepKey,
    payMonth,
    processedIds.join(","),
    companyScopeEmployees.length,
    onEnsureAttendanceCounts,
    stepBusy,
  ]);

  // Counts from FE attendance universe only (same list as register / chooser).
  // Do not prefer processStatus.totalCount — that is a wider company scope and caused 27 vs FE.
  const processedCount = processedEmployees.length;
  const unprocessedCount = unprocessedEmployees.length;
  const companyTotalCount = companyScopeEmployees.length;
  /** Same universe as the Process grid — one unprocessed employee unlocks Change Status. */
  const stepsFullyLocked =
    companyTotalCount > 0 &&
    unprocessedCount === 0 &&
    processedCount === companyTotalCount;
  /** Server scope — Finalise / lock API still requires this independently. */
  const serverFullyLocked = !!processStatus?.stepsFullyLocked;
  const hasAnyProcessed = processedCount > 0;
  const monthFinalised = !!(isLocked || processState.finalisedAt);

  useEffect(() => {
    if (!payMonth) return;
    const month = String(payMonth).slice(0, 7);
    const marker = `${month}|${monthFinalised ? 1 : 0}`;
    if (autoLandingMonthRef.current === marker) return;
    autoLandingMonthRef.current = marker;
    const land = resolveProcessWizardLanding({
      steps: processState.steps || [],
      storageKey: `attendance-wizard-step:${month}`,
      monthFinalised,
      payslipsPublished: false,
    });
    if (!land) return;
    setProcessState((prev) => {
      if (prev.activeStepKey === land) return prev;
      return { ...prev, activeStepKey: land };
    });
    persistProcessWizardLanding(`attendance-wizard-step:${month}`, land);
  }, [payMonth, monthFinalised, processState.steps]);

  // Keep step colors and locks truthful to month processing state.
  useEffect(() => {
    if (monthFinalised) return;
    setProcessState((prev) => {
      const prevSteps = Array.isArray(prev.steps) ? prev.steps : [];
      if (!prevSteps.length) return prev;
      let changed = false;
      const nextSteps = prevSteps.map((step) => {
        const key = step.key;
        const current = String(step.status || "pending");

        if (key === "process") {
          const expected = hasAnyProcessed ? "passed" : "pending";
          if (current !== expected) {
            changed = true;
            return { ...step, status: expected };
          }
          return step;
        }

        if (!stepsFullyLocked && ATTENDANCE_REQUIRES_FULL_PROCESS_KEYS.has(key)) {
          if (current !== "pending") {
            changed = true;
            return { ...step, status: "pending" };
          }
          return step;
        }

        return step;
      });

      return changed ? { ...prev, steps: nextSteps } : prev;
    });
  }, [hasAnyProcessed, stepsFullyLocked, monthFinalised, payMonth]);

  /** Pre-process steps cannot Validate / edit once everyone is processed (Unprocess to unlock). */
  const activeStepKeyValue = activeStep?.key;
  const requiresFullProcessBeforeEdit =
    ATTENDANCE_REQUIRES_FULL_PROCESS_KEYS.has(activeStepKeyValue);
  const stepEditingLocked = requiresFullProcessBeforeEdit
    ? !stepsFullyLocked || monthFinalised
    : ATTENDANCE_REVIEW_WHEN_LOCKED_KEYS.has(activeStepKeyValue)
      ? false
      : (stepsFullyLocked && !isAttendancePostProcessStep(activeStepKeyValue)) ||
        monthFinalised;

  /**
   * Existing header Refresh icon: revert this step’s validation only (not re-Validate).
   */
  const refreshActiveStep = () => {
    if (!activeStep?.key || monthFinalised) return;
    if (activeStep.key === "finalise" || activeStep.key === "process") return;
    if (stepEditingLocked) {
      toast.error(
        "All employees are processed. Unprocess some before refreshing this step."
      );
      return;
    }
    clearStepValidation(activeStep.key);
    toast.success(
      `${activeStep.label}: validation cleared — Validate again when ready.`
    );
  };

  const openActionChooser = (mode) => {
    if (monthFinalised) {
      toast.error("Month is locked. Unlock before process / unprocess.");
      return;
    }
    setActionChooser({ open: true, mode });
  };

  const confirmAction = async (payload) => {
    const mode = actionChooser.mode;
    const ids = (payload?.employeeIds || []).map(String).filter(Boolean);
    if (!ids.length) {
      toast.error("Select at least one employee");
      return;
    }
    if (!payMonth) {
      toast.error("Select an attendance month");
      return;
    }
    const numericIds = ids.map(Number).filter((id) => id > 0);
    const action =
      mode === "unprocess" ? "unprocess" : mode === "reprocess" ? "reprocess" : "process";

    setActionChooser({ open: false, mode: null });
    setStepBusy(true);
    setBatchProgress({ action, done: 0, total: numericIds.length });

    const chunkSize = processChunkSize(numericIds.length);
    const chunks = chunkIds(numericIds, chunkSize);
    const poolSize = processPoolConcurrency(numericIds.length, chunkSize);
    let done = 0;
    let healFailed = 0;
    let processFailed = false;

    const bumpProgress = (count) => {
      done += count;
      setBatchProgress({
        action,
        done,
        total: numericIds.length,
      });
    };

    try {
      if (mode === "unprocess") {
        resetStepsThroughProcess();
      }

      await runChunksInPool(chunks, poolSize, async (chunk) => {
        if (processFailed) return;
        if (mode === "process" || mode === "reprocess") {
          try {
            const runRes = await attendanceService.runAttendanceEngine({
              payMonth,
              employeeIds: chunk,
              skipProcessStatus: true,
            });
            setLastRunSummary(runRes?.data || runRes);
            setProcessStatus((prev) => {
              if (!prev) return prev;
              const next = new Set(
                (prev.processedEmployeeIds || []).map((id) => Number(id))
              );
              chunk.forEach((id) => next.add(Number(id)));
              const processedEmployeeIds = [...next];
              return {
                ...prev,
                processedEmployeeIds,
                processedCount: processedEmployeeIds.length,
                unprocessedCount: Math.max(
                  0,
                  (prev.totalCount || 0) - processedEmployeeIds.length
                ),
              };
            });
          } catch (runErr) {
            processFailed = true;
            toast.error(
              runErr?.response?.data?.message ||
                runErr?.message ||
                `Attendance sync failed at ${done}/${numericIds.length} — remaining not marked`
            );
            return;
          }
        } else {
          try {
            const runRes = await attendanceService.runAttendanceEngine({
              payMonth,
              employeeIds: chunk,
              skipMonthProcessMark: true,
              unprocessHeal: true,
              skipProcessStatus: true,
            });
            setLastRunSummary(runRes?.data || runRes);
          } catch (runErr) {
            healFailed += chunk.length;
            try {
              await attendanceService.updateAttendanceProcessEmployees({
                payMonth,
                action: "unprocess",
                employeeIds: chunk,
              });
            } catch {
              /* marks may already be cleared */
            }
          }
          setProcessStatus((prev) => {
            if (!prev) return prev;
            const drop = new Set(chunk.map(Number));
            const processedEmployeeIds = (prev.processedEmployeeIds || [])
              .map(Number)
              .filter((id) => !drop.has(id));
            return {
              ...prev,
              processedEmployeeIds,
              processedCount: processedEmployeeIds.length,
              unprocessedCount: Math.max(
                0,
                (prev.totalCount || 0) - processedEmployeeIds.length
              ),
            };
          });
        }

        bumpProgress(chunk.length);
      });

      const statusAfter = await refreshProcessStatus();

      // Refresh day rows so Process PR/LOP/Pay Days match calendar (full history + punches).
      if (typeof onEnsureAttendanceCounts === "function") {
        try {
          await onEnsureAttendanceCounts(numericIds);
        } catch {
          /* non-blocking */
        }
      }

      if (mode === "unprocess") {
        toast.success(`Unprocessed ${done} employee(s)`);
        if (healFailed > 0) {
          toast.error(
            `Attendance re-heal timed out or failed for ${healFailed} employee(s). Marks were cleared — open calendar or Process again if days look stale.`
          );
        }
        const stillLockedOnGrid =
          Array.isArray(statusAfter?.processedEmployeeIds) &&
          numericIds.every((id) =>
            (statusAfter.processedEmployeeIds || []).map(String).includes(String(id))
          );
        if (stillLockedOnGrid) {
          toast.error(
            "Month still shows those employees as processed after Unprocess. Refresh and try again."
          );
        }
      } else if (done > 0) {
        toast.success(
          mode === "reprocess"
            ? `Synced & reprocessed ${done} employee(s)`
            : `Synced & processed ${done} employee(s)`
        );
        markStep("process", "passed");
      }
    } catch (err) {
      await refreshProcessStatus();
      toast.error(err?.response?.data?.message || err?.message || "Process update failed");
    } finally {
      setStepBusy(false);
      setBatchProgress(null);
    }
  };

  const runValidate = async () => {
    if (!activeStep) return;
    if (
      stepsFullyLocked &&
      !isAttendancePostProcessStep(activeStep.key) &&
      !ATTENDANCE_REVIEW_WHEN_LOCKED_KEYS.has(activeStep.key)
    ) {
      toast.error(
        "All employees are processed. Unprocess some before editing earlier steps."
      );
      return;
    }
    if (activeStep.key === "checklist" && !stepsFullyLocked) {
      toast.error("Process all company employees before completing the checklist.");
      return;
    }
    if (activeStep.key === "preview_validate" && !stepsFullyLocked) {
      toast.error("Process all company employees before preview and validate.");
      return;
    }
    if (activeStep.key === "finalise" && !serverFullyLocked) {
      toast.error(
        `Process all company employees before finalising (${processedCount}/${companyTotalCount} processed).`
      );
      return;
    }
    if (monthFinalised && activeStep.key !== "finalise" && activeStep.key !== "preview_validate") {
      toast.error("Month is finalised / locked. Unlock from Finalise before editing.");
      return;
    }
    setStepBusy(true);
    try {
      if (activeStep.key === "finalise") {
        if (isLocked || processState.finalisedAt) {
          markStep(activeStep.key, "passed");
        } else if (typeof onLockMonth === "function") {
          try {
            await onLockMonth();
            markStep(activeStep.key, "passed");
          } catch (err) {
            if (err?.message !== "Lock cancelled") {
              toast.error(
                err?.response?.data?.message || err?.message || "Failed to finalise attendance"
              );
            }
            markStep(activeStep.key, "failed");
          }
        } else {
          markStep(activeStep.key, "failed");
        }
      } else {
        markStep(activeStep.key, "passed");
      }
    } finally {
      setStepBusy(false);
    }
  };

  const goNext = () => {
    const next = steps[activeIndex + 1];
    if (next) setActiveStepKey(next.key);
  };

  const goPrev = () => {
    const prev = steps[activeIndex - 1];
    if (!prev) return;
    // After process/finalise, browse freely — do not wipe completed checks.
    if (!monthFinalised && !stepsFullyLocked) {
      clearStepValidation(activeStep?.key);
    }
    setActiveStepKey(prev.key);
  };

  const card = darkMode
    ? "rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-3"
    : "rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-3 shadow-sm";

  const stepsWithoutValidate = new Set(["process", "finalise"]);

  const chooserPool =
    actionChooser.mode === "unprocess" || actionChooser.mode === "reprocess"
      ? processedEmployees
      : unprocessedEmployees;

  const panelExtraActions =
    activeStep?.key === "process" ? (
      <ProcessAttendanceActions
        darkMode={darkMode}
        unprocessedCount={unprocessedCount}
        processedCount={processedCount}
        stepBusy={stepBusy}
        onOpenAction={openActionChooser}
        isLocked={monthFinalised}
      />
    ) : activeStep?.key === "finalise" ? (
      <FinaliseAttendanceActions
        darkMode={darkMode}
        stepBusy={stepBusy || processStatusLoading}
        isLocked={monthFinalised}
        stepsFullyLocked={serverFullyLocked}
        processedCount={processedCount}
        totalCount={companyTotalCount}
        canFinalise={typeof onLockMonth === "function"}
        onFinalise={runValidate}
        onRequestUnlock={onRequestUnlock}
        workflowMethod={workflowMethod}
        pendingLockRequest={pendingFinalizationRequests.lock}
        pendingUnlockRequest={pendingFinalizationRequests.unlock}
      />
    ) : null;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden">
      <div className="flex shrink-0 flex-wrap items-end justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-1">
            <div className="flex items-center gap-2">
            <h2
              className={`text-base font-semibold ${
                darkMode ? "text-[hsl(var(--ds-fg))]" : "text-[hsl(var(--ds-secondary))]"
              }`}
            >
              {panelView === "actions"
                ? "Attendance actions"
                : "Process & finalise attendance"}
              {panelView === "process" && payMonth
                ? ` — ${formatPayMonthLabel(payMonth)}`
                : ""}
            </h2>
            {panelView === "process" ? (
              <ProcessHintInfo
                text={PROCESS_FLOW_HINT}
                darkMode={darkMode}
                ariaLabel="Process steps overview"
              />
            ) : null}
          </div>
          {panelView === "actions" ? (
            <div className="group/info relative">
              <Info
                size={15}
                className="cursor-pointer text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))]"
              />

              <div className="pointer-events-none absolute left-full top-1/2 z-[9999] ml-2 hidden w-[400px] -translate-y-1/2 rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 py-2 text-sm text-[hsl(var(--ds-fg))] shadow-lg group-hover/info:block">
                Click Open on an action. Choose the employee by search inside that form.
              </div>
            </div>
          ) : null}
          </div>
          {(actionsHub || legacyRegister) && canProcessWorkflow ? (
            <div className="mt-2 inline-flex rounded-full border border-[hsl(var(--ds-border))] p-0.5">
              <button
                type="button"
                onClick={() => setPanelView("actions")}
                className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                  panelView === "actions"
                    ? "bg-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary-fg))]"
                    : "text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))]"
                }`}
              >
                Actions
              </button>
              <button
                type="button"
                onClick={() => setPanelView("process")}
                className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                  panelView === "process"
                    ? "bg-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary-fg))]"
                    : "text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))]"
                }`}
              >
                Process
              </button>
            </div>
          ) : null}
        </div>
        <div className="flex flex-wrap items-end gap-3">
          {/* {panelView === "process" && lastRunSummary ? (
            <span className="mb-0.5 text-[11px] text-[hsl(var(--ds-muted-fg))]">
              Last sync: {lastRunSummary.processedCount ?? 0} emp ·{" "}
              {lastRunSummary.exceptionCount ?? 0} exc · OT {lastRunSummary.otHours ?? 0}h
            </span>
          ) : null} */}
          {panelView === "process" && monthFinalised ? (
            <span className="mb-0.5 inline-flex items-center rounded-full border border-emerald-500/40 bg-emerald-500 px-3 py-1 text-xs font-bold uppercase tracking-wide text-white shadow-sm">
              {processState.finalisedAt ? "Finalised" : "Locked"}
            </span>
          ) : null}
          {panelView === "process" && !monthFinalised && stepsFullyLocked ? (
            <span className="mb-0.5 inline-flex items-center rounded-full border border-sky-500/40 bg-sky-600 px-3 py-1 text-xs font-bold uppercase tracking-wide text-white shadow-sm">
              All company employees processed ({processedCount}/{companyTotalCount})
            </span>
          ) : panelView === "process" && !monthFinalised && companyTotalCount > 0 ? (
            <span className="mb-0.5 text-[11px] text-[hsl(var(--ds-muted-fg))]">
              Processed {processedCount}/{companyTotalCount} company employees
            </span>
          ) : null}
          {panelView === "process" ? (
            <PayMonthNav
              id="process-attendance-month"
              label="Attendance month"
              value={payMonth || ""}
              min={companyMinMonth || ""}
              max={companyMaxMonth || ""}
              darkMode={darkMode}
              onChange={handlePayMonthChange}
            />
          ) : null}
        </div>
      </div>

      {panelView === "actions" && (actionsHub || legacyRegister) ? (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          {actionsHub || legacyRegister}
        </div>
      ) : (
      <ProcessWorkflowShell
        accent="attendance"
        wizard={
          <ProcessWizardStrip
            steps={steps}
            activeKey={activeStepKey}
            visitedKeys={visitedKeys}
            onSelect={setActiveStepKey}
            accent="attendance"
            darkMode={darkMode}
            statusLabel={
              monthFinalised
                ? "finalised"
                : stepsFullyLocked
                  ? "processed"
                  : processState.status || "draft"
            }
            viewLocked={monthFinalised || stepsFullyLocked}
            actionableKeys={["process", "finalise"]}
          />
        }
      >
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div className="mb-2 flex shrink-0 flex-nowrap items-center gap-2 overflow-x-auto">
            <div className="flex min-w-0 items-center gap-1">
              <h3 className="min-w-0 truncate text-sm font-semibold">
                {activeStep?.label || "Step"}
              </h3>
              <ProcessHintInfo
                key={activeStep?.key}
                text={stepHintText(activeStep)}
                darkMode={darkMode}
                ariaLabel={`${activeStep?.label || "Step"} details`}
              />
            </div>
            {stepEditingLocked &&
              !isAttendancePostProcessStep(activeStep?.key) &&
              !ATTENDANCE_REVIEW_WHEN_LOCKED_KEYS.has(activeStep?.key) && (
                <span className="shrink-0 rounded-full bg-[hsl(var(--ds-muted))] px-2.5 py-1 text-[10px] font-semibold uppercase text-[hsl(var(--ds-muted-fg))] dark:bg-[hsl(var(--ds-muted))] dark:text-[hsl(var(--ds-fg))]">
                  Locked — all processed
                </span>
              )}
            {activeStep?.key !== "finalise" &&
              activeStep?.key !== "process" &&
              !monthFinalised &&
              !stepEditingLocked && (
                <button
                  type="button"
                  disabled={stepBusy}
                  onClick={refreshActiveStep}
                  title="Refresh this step — clear validation (Validate again when ready)"
                  aria-label="Refresh step"
                  className={`ml-auto inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border shadow-sm disabled:opacity-40 ${
                    darkMode
                      ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))] hover:border-[hsl(var(--ds-primary))] hover:text-[hsl(var(--ds-primary))]"
                      : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))] hover:border-[hsl(var(--ds-primary))] hover:text-[hsl(var(--ds-primary))]"
                  }`}
                >
                  {stepBusy ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <RefreshCw size={14} />
                  )}
                </button>
              )}
          </div>
          {stepsFullyLocked &&
            !isAttendancePostProcessStep(activeStep?.key) &&
            !ATTENDANCE_REVIEW_WHEN_LOCKED_KEYS.has(activeStep?.key) &&
            !monthFinalised && (
              <p
                className={`mb-2 shrink-0 rounded border px-2.5 py-1.5 text-[11px] ${
                  darkMode
                    ? "border-amber-700/50 bg-amber-950/30 text-amber-100"
                    : "border-amber-200 bg-amber-50 text-amber-900"
                }`}
              >
                All employees are processed. Unprocess from the Process Attendance step before
                editing Change Status, Leave, LOP, or Reconciliation.
              </p>
            )}
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <div className="min-h-0 flex-1 overflow-auto pr-0.5">
              <ProcessAttendanceStepBody
                stepKey={activeStep?.key}
                darkMode={darkMode}
                payMonth={payMonth}
                employees={companyScopeEmployees}
                isLocked={monthFinalised}
                editingLocked={stepEditingLocked}
                stepsFullyLocked={stepsFullyLocked}
                serverFullyLocked={serverFullyLocked}
                finalisedAt={processState.finalisedAt}
                includeApplyLeave={includeApplyLeave}
                statusColumns={statusColumns}
                leaveCodes={leaveCodes}
                getStatusCounts={getStatusCounts}
                getPayDays={getPayDays}
                processCountsRevision={processCountsRevision}
                policies={policies}
                scopeMode={scopeMode}
                selectedPolicyIds={selectedPolicyIds}
                onScopeChange={({ scopeMode: m, selectedPolicyIds: ids }) => {
                  setScopeMode(m || "all");
                  setSelectedPolicyIds(ids || []);
                }}
                onOpenCalendar={onOpenCalendar}
                onOpenCalendarQueue={onOpenCalendarQueue}
                onApplyLeave={onApplyLeave}
                onLopReversal={onLopReversal}
                onExportExcel={onExportExcel}
                onRequestUnlock={onRequestUnlock}
                processedCount={processedCount}
                totalCount={companyTotalCount}
                processedEmployeeIds={processStatus?.processedEmployeeIds || []}
                batchProgress={batchProgress}
              />
            </div>
            <div className="shrink-0">
              <PayrollStepActions
                step={activeStep}
                stepBusy={stepBusy}
                employeeCount={scopedEmployees.length || 1}
                onRunCheck={runValidate}
                onPrevious={goPrev}
                onNext={goNext}
                canGoPrevious={activeIndex > 0}
                canGoNext={activeIndex < steps.length - 1}
                darkMode={darkMode}
                disabled={
                  monthFinalised
                    ? !["process", "finalise"].includes(activeStep?.key)
                    : stepEditingLocked
                }
                showValidate={
                  !stepsWithoutValidate.has(activeStep?.key) &&
                  !monthFinalised &&
                  !stepEditingLocked
                }
                extraActions={panelExtraActions}
              />
            </div>
          </div>
        </div>
      </ProcessWorkflowShell>
      )}

      <PayrollActionChooserModal
        darkMode={darkMode}
        open={actionChooser.open}
        title={
          actionChooser.mode === "unprocess"
            ? "Unprocess attendance"
            : actionChooser.mode === "reprocess"
            ? "Reprocess attendance"
            : "Process attendance"
        }
        subtitle={
          actionChooser.mode === "unprocess" || actionChooser.mode === "reprocess"
            ? "Choose processed employees (default: all)"
            : "Choose unprocessed employees (default: all)"
        }
        employees={chooserPool.map((e) => ({
          ...e,
          empCode: e.empCode || e.employeeId || e.code,
          name:
            e.user?.name ||
            e.name ||
            [e.firstName, e.lastName].filter(Boolean).join(" "),
        }))}
        masterLists={masterLists}
        confirmLabel={
          actionChooser.mode === "unprocess"
            ? "Unprocess"
            : actionChooser.mode === "reprocess"
            ? "Reprocess"
            : "Process"
        }
        onClose={() => setActionChooser({ open: false, mode: null })}
        onConfirm={confirmAction}
      />
    </div>
  );
}

export { ATTENDANCE_PROCESS_STEPS } from "../../../constants/attendanceProcessCatalog";
